/**
 * Demolição síncrona da sessão local quando o refresh falha (rede caída,
 * gateway down, 4xx, 5xx, token adulterado, abort, timeout).
 *
 * Extraído de useAuth para que o fetch interceptor consiga reagir
 * IMEDIATAMENTE a falhas em /auth/v1/token?grant_type=refresh_token,
 * sem depender de eventos do supabase-js (que não disparam em abort/hang).
 *
 * Idempotente: o flag `__zappy_teardown_running` evita execuções
 * simultâneas (e replaces concorrentes para a entry pública).
 */
import { supabase } from "@/integrations/supabase/client";
import { startServerSessionCleanup } from "@/lib/auth/serverSessionCleanup";
import {
  APP_ENTRY_ROUTE,
  clearAccessFlowLock,
} from "@/lib/access-flow";
import { clearPersistedSession } from "@/lib/sessionPersistence";
import {
  consumeVoluntarySignout,
  markSessionExpired,
  type SessionExpiredReason,
} from "@/lib/session-expired-notice";

type TeardownReason = Extract<SessionExpiredReason, "refresh_failed" | "remote_revoked">;

const FLAG = "__zappy_teardown_running__";

export const tearDownLocalSession = async (
  reason: TeardownReason = "refresh_failed",
): Promise<void> => {
  if (typeof window !== "undefined") {
    const w = window as unknown as Record<string, unknown>;
    if (w[FLAG]) return;
    w[FLAG] = true;
  }

  let accessToken: string | null = null;
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key?.startsWith("sb-") && key.endsWith("-auth-token")) {
        const stored = JSON.parse(window.localStorage.getItem(key) ?? "null");
        if (typeof stored?.access_token === "string") accessToken = stored.access_token;
      }
    }
  } catch { /* malformed or unavailable storage */ }
  // Start release with a captured bearer before synchronously clearing storage.
  const serverCleanup = startServerSessionCleanup(accessToken, import.meta.env.VITE_SUPABASE_URL, import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY);
  window.dispatchEvent(new Event("zappy:session-invalidated"));
  try { clearAccessFlowLock(); } catch { /* noop */ }
  try { clearPersistedSession(); } catch { /* noop */ }
  // Remoção SÍNCRONA da chave de auth do supabase-js. Sem isso, se o
  // signOut() ficar travado (rede caída, refresh interceptado), a
  // sessão revogada persiste no localStorage e ressuscita no próximo
  // boot.
  try {
    if (typeof window !== "undefined") {
      const ls = window.localStorage;
      const removable: string[] = [];
      for (let i = 0; i < ls.length; i++) {
        const key = ls.key(i);
        if (key && key.startsWith("sb-") && key.endsWith("-auth-token")) {
          removable.push(key);
        }
      }
      removable.forEach((k) => ls.removeItem(k));
    }
  } catch { /* noop */ }
  if (!consumeVoluntarySignout()) {
    try { markSessionExpired(reason); } catch { /* noop */ }
  }

  // signOut em paralelo com cap de 1500ms — não pode bloquear o redirect
  // se a rede sumiu (o próprio /logout pode estar offline).
  void supabase.auth.signOut({ scope: "local" }).catch(() => undefined);
  await Promise.race([
    serverCleanup,
    new Promise((resolve) => setTimeout(resolve, 1500)),
  ]);

  try {
    if (typeof window !== "undefined" && window.location.pathname !== APP_ENTRY_ROUTE) {
      window.location.replace(APP_ENTRY_ROUTE);
    }
  } catch { /* ignore */ }
};
